<script lang="ts" setup>
import CESGRoles from '@/components/common/roles/CESGRoles.vue';
import SimpleTable from '@/components/tables/SimpleTable.vue';
import type { BaseColumnData } from '@/utils/tables/column-data';
import { PhCheck } from '@phosphor-icons/vue';
import { UserRole } from 'cesg-shared/dist/models';
import { useI18n } from 'vue-i18n';
import type {
  User as ModalUser,
  UserModalAction,
  UserModalViewProps,
} from './user-modal/UserModal.vue';
import UserModal from './user-modal/UserModal.vue';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  projects: string;
}

export interface UsersManagmentViewProps {
  data: {
    users: User[];
    allRoles: UserRole[];
    selectedRoles: UserRole[];
    selectedUser: ModalUser | null;
    availableRoles?: UserModalViewProps['data']['availableRoles'];
  };
}

export type UsersManagmentAction =
  | { type: 'rowClick'; userId: string }
  | { type: 'toggleRole'; role: UserRole }
  | { type: 'modalAction'; action: UserModalAction };

const { t } = useI18n();

const props = defineProps<UsersManagmentViewProps>();
const emit = defineEmits<{
  (event: 'action', payload: UsersManagmentAction): void;
}>();

const columns: BaseColumnData[] = [
  { key: 'name', label: t('name'), sortable: true },
  { key: 'email', label: t('email'), sortable: true },
  { key: 'projects', label: t('project'), sortable: true },
  { key: 'role', label: t('role'), sortable: true, align: 'has-text-right' },
];
</script>

<template>
  <section class="users-management-table">
    <div class="role-filter">
      <button
        v-for="role in props.data.allRoles"
        :key="role"
        type="button"
        class="role-filter-pill"
        :class="{ 'is-active': props.data.selectedRoles.includes(role) }"
        @click="emit('action', { type: 'toggleRole', role })"
      >
        <span class="role-check">
          <PhCheck
            v-if="props.data.selectedRoles.includes(role)"
            :size="11"
            weight="bold"
          />
        </span>
        <CESGRoles :data="{ role }" />
      </button>
    </div>
    <SimpleTable
      :columns="columns"
      :data="props.data.users"
      :enable-search="true"
    >
      <template #tbody="{ data }">
        <tr
          v-for="user in data"
          :key="user.id"
          @click="emit('action', { type: 'rowClick', userId: user.id })"
          class="user-row"
        >
          <td>
            <div class="name-cell">{{ user.name }}</div>
          </td>
          <td>
            <div class="email-cell" :title="user.email">{{ user.email }}</div>
          </td>
          <td>
            <div class="projects-cell" :title="user.projects">
              <span v-if="user.projects">{{ user.projects }}</span>
              <span v-else>-</span>
            </div>
          </td>
          <td class="has-text-right">
            <CESGRoles
              class="role-pill"
              :data="{
                role: user.role,
              }"
            />
          </td>
        </tr>
      </template>
    </SimpleTable>
    <UserModal
      v-if="props.data.selectedUser"
      :data="{
        user: props.data.selectedUser,
        availableRoles: props.data.availableRoles,
      }"
      :hide="{
        projects: props.data.selectedUser?.role === UserRole.ADMIN,
      }"
      @action="
        (action: UserModalAction) =>
          emit('action', { type: 'modalAction', action })
      "
    />
  </section>
</template>

<style scoped lang="scss">
.role-filter {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.role-filter-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 2px solid #d1d5db;
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  background: #f3f4f6;
  color: #6b7280;
  transition:
    border-color 0.12s,
    background 0.12s,
    color 0.12s;

  &.is-active {
    border-color: var(--primary, #485fc7);
    background: rgba(72, 95, 199, 0.12);
    color: var(--primary, #485fc7);
  }
}

.role-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  border: 1.5px solid currentColor;
  flex-shrink: 0;

  .is-active & {
    background: var(--primary, #485fc7);
    border-color: var(--primary, #485fc7);
    color: white;
  }
}

.users-management-table {
  :deep(.table) {
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid var(--table-border-color, #e7e7e7);
    background: var(--color-white, #ffffff);
  }

  :deep(thead th) {
    background: var(--table-header-background, #f7f9fc);
    font-weight: 600;
    letter-spacing: 0.01em;
  }

  :deep(tbody td) {
    vertical-align: middle;
    padding-top: 0.8rem;
    padding-bottom: 0.8rem;
  }

  :deep(tbody tr:nth-child(even)) {
    background: rgba(0, 0, 0, 0.015);
  }
}

.email-cell {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.name-cell {
  font-weight: 500;
}

.projects-cell {
  max-width: 380px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.role-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 90px;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--color-text-primary, #253041);
  background: var(--table-header-background, #f1f5fb);
}

.user-row {
  cursor: pointer;
}

.table-actions {
  position: sticky;
  width: fit-content;
  margin-left: auto;
  bottom: 1.5rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  z-index: 30;
  padding: 0.75rem;
  border-radius: 10px;
  backdrop-filter: blur(2px);
}

@media screen and (max-width: 768px) {
  .table-actions {
    width: 100%;
    margin-left: 0;
    bottom: 0.75rem;
    justify-content: flex-end;
  }
}
</style>
